import { TextField } from '@mui/material';
import type { MRT_ColumnDef } from 'material-react-table';
import { ChangeEvent, useMemo } from 'react';

import {
  ESTADOS_RUBRO_CHOICE,
  TIPOS_RUBRO_CHOICE,
  formatCurrencyCell,
  formatDateCell,
  formatDateWithTimeCell,
  formatQuantityCell,
  humanizeStateChoice,
} from '@/shared';
import type { Contrato, Rubro, RubroProductDetail } from '@/shared/interfaces';
import { type IUbicacionProducto } from '@/shared/interfaces/erp/inventario';
import {
  ClienteRubroActionBtns,
  ClienteRubroChipState,
  ViewClienteRubroInfoTableCell,
} from '../components/tables/rubros';

type UseColumnsRubrosClienteProps = {
  contract: Contrato;
  onChangeCode?: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    currentItem: ClienteRubroLibreTableType
  ) => void;
  onChangeDescription?: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    currentItem: ClienteRubroLibreTableType
  ) => void;
};

export type ClienteRubroLibreTableType = IUbicacionProducto & {
  id?: string;

  precio_1: number;
  ivaCode: string;
  ivaValue: number;
  cantidadUtilizada: number;

  contieneSeries: boolean;
  selectedSeries: string[];
  savedSeries: string[];
  seriesAll: string[];

  isNewLine?: boolean; // for new empty line

  ///* fk -- upd rubro
  bodega?: string | null;
  ubicacion?: string | null;

  // --- upd
  isUpdating?: boolean;
  canUpdate?: boolean;
};

export type ClienteRubroServicioTableType = {
  id?: string;
  descripcion: string;
  precio_1: number;
  cantidad: number;

  codigo?: string; // plan code

  total: number;

  isPlan?: boolean;

  isIVA?: boolean;
  ivaCode: string;
  ivaValue: number;

  isAdminCost?: boolean;

  isNotFoundParam?: boolean;
  isParameter?: boolean;
};

export const useColumnsRubrosCliente = ({
  contract,
  onChangeCode,
  onChangeDescription,
}: UseColumnsRubrosClienteProps) => {
  ///* rubros ==============================
  const columnsRubrosClientView = useMemo<MRT_ColumnDef<Rubro>[]>(
    () => [
      {
        accessorKey: 'numero_referencia',
        header: 'N° Rubro',
        size: 200,
        Cell: ({ row }) => {
          return (
            <ViewClienteRubroInfoTableCell
              rubro={row.original}
              contract={contract!}
            />
          );
        },
      },

      {
        accessorKey: 'fecha_emision',
        header: 'Emitido',
        size: 200,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_emision'),
        filterVariant: 'date',
        enableSorting: false,
      },
      {
        accessorKey: 'fecha_vencimiento',
        header: 'Fecha de Vencimiento',
        size: 200,
        Cell: ({ row }) => formatDateCell(row, 'fecha_vencimiento'),
        filterVariant: 'date',
        enableSorting: false,
      },

      {
        accessorKey: 'estado_rubro',
        header: 'Estado',
        size: 210,
        Cell: ({ row }) => {
          return <ClienteRubroChipState state={row.original?.estado_rubro} />;
        },
        filterVariant: 'select',
        filterSelectOptions: ESTADOS_RUBRO_CHOICE?.map(state => ({
          label: humanizeStateChoice(state),
          value: state,
        })),
      },

      {
        accessorKey: 'valor_total',
        header: 'Total',
        size: 180,
        Cell: ({ row }) => {
          return formatCurrencyCell(row, 'valor_total');
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: 'valor_iva',
        header: 'Impuestos',
        size: 180,
        Cell: ({ row }) => {
          return formatCurrencyCell(row, 'valor_iva');
        },
        enableColumnFilter: false,
        enableSorting: false,
      },

      {
        accessorKey: 'tipo_rubro',
        header: 'Tipo',
        size: 200,
        filterVariant: 'select',
        filterSelectOptions: TIPOS_RUBRO_CHOICE?.map(type => ({
          label: humanizeStateChoice(type),
          value: type,
        })),
        enableSorting: false,
      },

      {
        accessorKey: 'valor_pagado',
        header: 'Valor Pagado',
        size: 180,
        Cell: ({ row }) => {
          return formatCurrencyCell(row, 'valor_pagado');
        },
        enableColumnFilter: false,
        enableSorting: false,
      },

      {
        accessorKey: 'fecha_pago',
        header: 'Fecha de Pago',
        size: 200,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_pago'),
        filterVariant: 'date',
        enableSorting: false,
      },

      ///* custom actions ------------
      {
        accessorKey: 'custom_actions',
        header: 'Acciones',
        size: 300,
        muiTableHeadCellProps: {
          align: 'center',
        },
        Cell: ({ row }) => {
          return (
            <ClienteRubroActionBtns rubro={row.original} contract={contract!} />
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );

  ///* rubro libre ==============================
  const columnsClienteRubrosLibreb01 = useMemo<
    MRT_ColumnDef<ClienteRubroLibreTableType>[]
  >(
    () => [
      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 222,
        Cell: ({ row }) => {
          const isNewLine = !!row.original?.isNewLine;

          if (isNewLine) {
            return (
              <TextField
                variant="outlined"
                value={row.original?.producto_data?.codigo || ''}
                onChange={e => {
                  onChangeCode && onChangeCode(e, row.original);
                }}
                fullWidth
              />
            );
          }

          return row.original?.producto_data?.codigo || 'N/A';
        },
      },
      {
        accessorKey: 'descripcion',
        header: 'Descripción',
        size: 222,
        Cell: ({ row }) => {
          const isNewLine = !!row.original?.isNewLine;

          if (isNewLine) {
            return (
              <TextField
                variant="outlined"
                value={row.original?.producto_data?.descripcion || ''}
                onChange={e => {
                  onChangeDescription && onChangeDescription(e, row.original);
                }}
                fullWidth
              />
            );
          }

          return row.original?.producto_data?.descripcion || 'N/A';
        },
      },
    ],
    []
  );

  ///* view rubros ==============================
  // only view rurbos --------------
  const columnsClienteRubrosView = useMemo<MRT_ColumnDef<RubroProductDetail>[]>(
    () => [
      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 222,
        Cell: ({ row }) => row.original?.producto_data?.codigo || 'N/A',
      },
      {
        accessorKey: 'descripcion',
        header: 'Descripción',
        size: 222,
        Cell: ({ row }) => row.original?.producto_data?.descripcion || 'N/A',
      },
      {
        accessorKey: 'price',
        header: 'Precio',
        size: 222,
        Cell: ({ row }) => formatCurrencyCell(row, 'precio_1'),
      },
      {
        accessorKey: 'impuestos',
        header: 'Imp %',
        size: 222,
        Cell: ({ row }) => {
          const ivaPercent = row.original?.ivaValue || 0;
          return `${ivaPercent}%`;
        },
      },
      {
        accessorKey: 'quantity',
        header: 'Cantidad',
        size: 222,
        Cell: ({ row }) => formatQuantityCell(row, 'cantidad'),
      },
      {
        accessorKey: 'total',
        header: 'Total',
        size: 222,
        Cell: ({ row }) => {
          return formatCurrencyCell(row, 'totalLine');
        },
      },
    ],
    []
  );

  return {
    columnsRubrosClientView,
    columnsClienteRubrosLibreb01,

    columnsClienteRubrosView,
  };
};
